import apiClient from './client'
import { TelaResponse, MenuItemResponse, TelaUpdate } from '../types/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('telasApi')

export const telasApi = {
  /**
   * Lista telas com filtros opcionais
   */
  list: async (params?: {
    empresa_id?: number
    tipo_sistema?: 'ADMIN' | 'GALATEA_WEB'
    ativo?: boolean
    incluir_inativos?: boolean
    skip?: number
    limit?: number
  }): Promise<TelaResponse[]> => {
    const response = await apiClient.get<TelaResponse[]>('/telas', { params })
    return response.data || []
  },

  /**
   * Obtém uma tela específica por ID
   */
  get: async (id: number): Promise<TelaResponse> => {
    const response = await apiClient.get<TelaResponse>(`/telas/${id}`)
    return response.data
  },

  /**
   * Obtém menu hierárquico de telas
   */
  getMenuHierarquico: async (params?: {
    tipo_sistema?: 'ADMIN' | 'GALATEA_WEB'
    empresa_id?: number
  }): Promise<TelaResponse[]> => {
    // SEM CACHE - sempre buscar dados frescos
    const response = await apiClient.get<TelaResponse[]>('/telas/menu/hierarquico', { 
      params,
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    })
    return response.data || []
  },

  /**
   * Verifica se usuário tem acesso a uma tela
   */
  verificarAcesso: async (
    telaId: number,
    params?: {
      empresa_id?: number
      area_funcional_id?: number
      nivel_acesso?: number
    }
  ): Promise<boolean> => {
    try {
      // Por enquanto, vamos usar a lista de telas e verificar se a tela está presente
      // O backend já filtra por permissões no endpoint de menu
      const telas = await telasApi.getMenuHierarquico({
        tipo_sistema: 'GALATEA_WEB',
        empresa_id: params?.empresa_id,
      })
      return telas.some((t) => t.id === telaId)
    } catch (error) {
      logger.error('Erro ao verificar acesso à tela:', error)
      return false
    }
  },

  /**
   * Obtém campos visíveis/editáveis para o usuário atual
   */
  getCamposUsuario: async (
    telaId: number,
    params?: {
      empresa_id?: number
      area_funcional_id?: number
    }
  ): Promise<Array<{ nome_campo: string; visivel: boolean; editavel: boolean }>> => {
    const response = await apiClient.get(`/telas/${telaId}/campos/usuario`, { params })
    return response.data || []
  },

  /**
   * Obtém estrutura de telas em árvore para um usuário específico
   */
  getArvoreUsuario: async (
    userId: number,
    params?: {
      tipo_sistema?: 'ADMIN' | 'GALATEA_WEB'
      empresa_id?: number
      area_funcional_id?: number
    }
  ): Promise<{ telas: MenuItemResponse[] }> => {
    // Garantir que userId é um número
    const userIdNum = typeof userId === 'string' ? parseInt(userId, 10) : Number(userId)
    if (isNaN(userIdNum)) {
      throw new Error(`userId deve ser um número válido, recebido: ${userId}`)
    }
    const response = await apiClient.get<{ telas: MenuItemResponse[] }>(
      `/telas/arvore/usuario/${userIdNum}`,
      { params }
    )
    return response.data || { telas: [] }
  },

  /**
   * Reordena múltiplas telas de uma vez
   */
  reordenar: async (
    telas: Array<{ id: number; posicao_menu: number }>,
    apenasGlobais: boolean = true
  ): Promise<TelaResponse[]> => {
    logger.debug('Enviando para API:', { telas, apenasGlobais })
    const response = await apiClient.put<TelaResponse[]>('/telas/reordenar', {
      telas: telas.map(t => ({ id: t.id, posicao_menu: t.posicao_menu })),
    }, {
      params: { apenas_globais: apenasGlobais }
    })
    logger.debug('Resposta da API:', response.data)
    return response.data || []
  },

  /**
   * Atualiza dados de uma tela específica
   */
  update: async (id: number, data: TelaUpdate): Promise<TelaResponse> => {
    const response = await apiClient.put<TelaResponse>(`/telas/${id}`, data)
    return response.data
  },
}

